import { useState, useEffect, useRef, useCallback } from 'react'
import { renderEmailTemplate } from '@/lib/api-client'
import { isComposeMode, getBodyAsync, prependBodyAsync } from '@/lib/office'

export type SignatureInjectStatus =
  | 'idle'
  | 'loading'
  | 'injected'
  | 'already-present'
  | 'no-template'
  | 'error'

const MARKER = 'data-athenty-injected="signature"'

async function injectOnce(): Promise<SignatureInjectStatus> {
  if (!isComposeMode()) return 'idle'

  const currentBody = await getBodyAsync('html')
  if (currentBody?.includes(MARKER)) return 'already-present'

  const result = await renderEmailTemplate('signature')
  if (result.error || !result.data) return 'error'

  const html = result.data.html.trim()
  if (!html) return 'no-template'

  const wrapped = `<div ${MARKER}>${html}</div>`
  const ok = await prependBodyAsync(wrapped)
  return ok ? 'injected' : 'error'
}

export function useSignatureInject() {
  const [status, setStatus] = useState<SignatureInjectStatus>('idle')
  // Prevent double-run in React 19 StrictMode and on rapid re-mounts.
  const hasRunRef = useRef(false)
  const [retryKey, setRetryKey] = useState(0)

  useEffect(() => {
    if (hasRunRef.current) return
    hasRunRef.current = true

    setStatus('loading')
    injectOnce().then(setStatus).catch(() => setStatus('error'))
  }, [retryKey])

  const retry = useCallback(() => {
    hasRunRef.current = false
    setRetryKey((k) => k + 1)
  }, [])

  return { status, retry }
}
